/**
 * Shared Hired / Rented Equipment UI helpers.
 * Mirrors Hire Agreement enterprise standards: human labels, status colors, linked option text.
 * Money display stays on formatHireMoney — never invent 0 for missing rate.
 */
import type { CSSProperties } from 'react';
import type {
  PmvHireAgreement,
  PmvHireCalculation,
  PmvHireStatement,
  PmvHireTimesheet,
} from '@/services/pmv-hire.service';
import { formatHireMoney } from '@/services/pmv-hire.service';
import { formatPmvProfileOption, type PmvProfileLabelSource } from '@/utils/pmvDisplay';

export const HIRE_STATUS_COLOR: Record<string, string> = {
  ON_HIRE: 'success',
  OFF_HIRE_REQUESTED: 'warning',
  OFF_HIRE_EFFECTIVE: 'orange',
  CLOSED: 'default',
  CANCELLED: 'error',
  PLANNED: 'default',
  CONFIRMED: 'blue',
  MOBILIZING: 'processing',
  DEMOBILIZING: 'processing',
  RETURNED: 'cyan',
};

export const HIRE_DOC_STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default',
  SUBMITTED: 'processing',
  UNDER_REVIEW: 'blue',
  CERTIFIED: 'success',
  RETURNED: 'warning',
  VOID: 'error',
  SUPERSEDED: 'default',
  OPEN: 'processing',
  MATCHED: 'success',
  VARIANCE: 'warning',
  DISPUTED: 'error',
  CLOSED: 'default',
  DISMISSED: 'default',
};

export const HIRE_COMMERCIAL_STATUSES = [
  'PLANNED',
  'CONFIRMED',
  'MOBILIZING',
  'ON_HIRE',
  'OFF_HIRE_REQUESTED',
  'OFF_HIRE_EFFECTIVE',
  'DEMOBILIZING',
  'RETURNED',
  'CLOSED',
  'CANCELLED',
] as const;

export type HireLifecycleAction =
  | 'on-hire'
  | 'off-hire-request'
  | 'off-hire-effective'
  | 'demobilize';

export const HIRE_LIFECYCLE_ACTION_LABEL: Record<HireLifecycleAction, string> = {
  'on-hire': 'Confirm on-hire',
  'off-hire-request': 'Request off-hire',
  'off-hire-effective': 'Set off-hire effective (charging stop)',
  demobilize: 'Record physical demobilization',
};

/** Status → which lifecycle actions are typically enabled. */
export function hireLifecycleActionsFor(status: string | undefined): HireLifecycleAction[] {
  switch (status) {
    case 'PLANNED':
    case 'CONFIRMED':
    case 'MOBILIZING':
      return ['on-hire'];
    case 'ON_HIRE':
      return ['off-hire-request'];
    case 'OFF_HIRE_REQUESTED':
      return ['off-hire-effective'];
    case 'OFF_HIRE_EFFECTIVE':
      return ['demobilize'];
    case 'DEMOBILIZING':
      return ['demobilize'];
    default:
      return [];
  }
}

export function unwrapApiList<T>(res: unknown): T[] {
  const body = (res as { data?: unknown })?.data ?? res;
  const inner = (body as { data?: unknown })?.data ?? body;
  return Array.isArray(inner) ? (inner as T[]) : [];
}

export function formatHireAgreementOption(a: PmvHireAgreement): string {
  return `${a.agreement_number} · ${a.commercial_status}${a.currency ? ` · ${a.currency}` : ''}`;
}

export function formatHireCalculationOption(c: PmvHireCalculation): string {
  const num = c.calculation_number || c.id.slice(0, 8);
  const period = `${c.period_from || '?'} → ${c.period_to || '?'}`;
  const amt =
    c.rate_available === false
      ? 'Rate unavailable'
      : formatHireMoney(c.total_amount, c.currency);
  return `${num} · ${period} · ${amt} · ${c.status}`;
}

export function formatHireTimesheetOption(t: PmvHireTimesheet): string {
  const num = t.timesheet_number || t.id.slice(0, 8);
  const period = `${t.period_from || '?'} → ${t.period_to || '?'}`;
  return `${num} · ${period} · ${formatHireMoney(t.total_amount, t.currency)} · ${t.status}`;
}

export function formatHireStatementOption(s: PmvHireStatement): string {
  const num = s.statement_number || s.id.slice(0, 8);
  return `${num} · ${formatHireMoney(s.total_amount, s.currency)} · ${s.status}`;
}

export function formatHireEquipmentLabel(p: PmvProfileLabelSource & { ownership_type?: string }): string {
  const base = formatPmvProfileOption(p);
  const fleet =
    p.fleet_number && !base.startsWith(String(p.fleet_number))
      ? `${p.fleet_number} · `
      : '';
  const own = p.ownership_type ? ` [${p.ownership_type}]` : '';
  return `${fleet}${base}${own}`;
}

export function buildIdLabelMap(
  rows: Array<{ id: string }>,
  labelOf: (row: { id: string } & Record<string, unknown>) => string
): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of rows) m.set(r.id, labelOf(r as { id: string } & Record<string, unknown>));
  return m;
}

export function resolveLabel(
  map: Map<string, string>,
  id: string | null | undefined,
  empty = '—'
): string {
  if (id == null || id === '') return empty;
  return map.get(id) || empty;
}

/** Cell wrapper so doc links / labels never bleed into the next table column. */
export const hireTableCellEllipsis: CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Ant Button type=link is inline-flex and overflows cells — pair with hireTableCellEllipsis wrapper. */
export const hireTableLinkBtnStyle: CSSProperties = {
  padding: 0,
  height: 'auto',
  lineHeight: '1.4',
  maxWidth: '100%',
};
